<script setup lang="ts">
import { AppSvgIcon, AppExpandableButton } from '@libs/ui';

import type { TripSummaryHeader } from './TripOverviewHeaderMobile.helper';

interface TripOverviewHeaderSummaryButtonProps {
  numberOfPax: number;
  calendarText: string;
  numberOfNights: number;
  headerSegments: TripSummaryHeader[];
}
const props = defineProps<TripOverviewHeaderSummaryButtonProps>();

function getKey(segment: TripSummaryHeader, index: number): string {
  return `${segment.text ? segment.text : 'no-text'} + ${
    segment.icon ? segment.icon : 'no-icon'
  } ${index}`;
}
</script>

<template>
  <div class="trip-overview-header-summary-button">
    <AppExpandableButton>
      <template #title>
        <div class="title-content">
          <div
            v-for="(segment, index) in props.headerSegments"
            :key="getKey(segment, index)"
            class="segment"
          >
            <AppSvgIcon
              v-if="segment.icon"
              class="icon"
              :name="segment.icon"
              size="16"
              color="color-soft-black"
            />
            <span v-if="segment.text" class="segment-text">
              {{ segment.text }}
            </span>
          </div>
        </div>
      </template>

      <template #subtitle>
        {{
          `${props.numberOfPax} ${$t(
            'planner.pax.travelers',
            props.numberOfPax
          ).toLocaleLowerCase()}, ${props.calendarText}, ${
            props.numberOfNights
          } ${$t('planner.nights', props.numberOfNights).toLowerCase()}`
        }}
      </template>

      <template #content>
        <slot />
      </template>
    </AppExpandableButton>
  </div>
</template>

<style scoped lang="scss">
.trip-overview-header-summary-button {
  margin: 0 var(--spacing-16) var(--spacing-16);
  padding: var(--spacing-16) var(--spacing-12) var(--spacing-16);
  border-radius: var(--border-radius-8);
  box-shadow: var(--shadow-elevation-20);
  border: 1px solid var(--color-neutral-30);

  @include for-less-or-equal-than-599-px {
    margin: 0 var(--spacing-12) var(--spacing-16) var(--spacing-12);
  }

  .title-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--spacing-4);
    max-width: 100%;
    overflow: hidden;

    .segment {
      display: flex;
      align-items: center;
      min-width: 0;

      .icon {
        transform: translateY(-1px);
        flex-shrink: 0;
      }

      .segment-text {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
    }
  }
}
</style>
